import React, {useEffect, useRef, useState} from "react";
import {Card, Table} from "react-bootstrap";
import {Col, Button, Spinner} from 'reactstrap';
import {useHistory} from "react-router";
import { useDispatch } from "react-redux";
import {getRoleLabels} from "../../constants/roles";
import Switch from '@mui/material/Switch';
import {API} from 'aws-amplify';
import {BUTTON_LABELS, MESSAGES, NOTIFICATION_MESSAGES} from "../../lang/ja";
import PaginationComponent from '../../components/PaginationComponent';
import {NUM_ITEMS_PER_PAGE} from "../../constants/table";
import { listUsersInGroups, handleDeleteUser } from "../../apis/UserApi";
import { useDialog } from "../../contexts/DialogContext.tsx";
import { useSelector } from "../../store/index.tsx";

const getVisibleUsers = (items) => {
  return items
    .filter(user => user?.isDeleted !== true)
    .filter((user, index, self) =>
      user?.id && self.findIndex(item => item?.id === user.id) === index
    );
};

const Accounts = () => {
  const { loading } = useSelector((state) => state.users);
  const history = useHistory();
  const governmentId = history.location.pathname.split('/')[3];
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [users, setUsers] = useState([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const nextTokenRef = useRef<string | null>(null);
  const { error } = useDialog();
  const dispatch = useDispatch(); // ここでdispatchを取得

  useEffect(() => {
    (async() => {
      await getUsers();
      setInitialLoad(false);
    })()
  }, []);

  const getUsers = async (shouldAppend = false) => {
    if (!shouldAppend) {
      nextTokenRef.current = null;
      setCurrentPage(0);
    }

    const res = await listUsersInGroups(governmentId, ["outsideAdminStaff"], nextTokenRef.current);
    if (res.success) {
      if (shouldAppend) {
        setUsers(prevUsers => getVisibleUsers([...prevUsers, ...res.data]));
      }
      else {
        setUsers(getVisibleUsers(res.data));
      }
      nextTokenRef.current = res.nextToken;
    }
    else {
      error(MESSAGES.SOMETHING_WRONG);
    }
  };

  const handleChangePage = async (newPage: number) => {
    setCurrentPage(newPage);
    if (newPage +1 === Math.ceil(users.length / NUM_ITEMS_PER_PAGE) && nextTokenRef.current) {
      await getUsers(true);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      const id = user.id;
      const enabled = user.enabled === null ? true : !user.enabled;
      const response = await API.post('userRestApi', '/toggle-user-status', {
        body: {
          userId: id,
          enabled
        }
      });
      if (response?.success) {
        const newUser = JSON.parse(response.data);
        users.splice(users.indexOf(user), 1, newUser);
        setUsers(Object.assign([], users));
      } else {
        console.error("Failed to toggle user status:", response?.message);
      }
    } catch (error) {
      console.error("Error calling the API:", error);
    }
  }

  const handleDeleteUserClick = async (id: string) => {
    await handleDeleteUser(id, dispatch, getUsers, (deletedUserId: string) => {
      setUsers(prevUsers => prevUsers.filter(user => user.id !== deletedUserId));
    });
  };

  return <Col md="12">
    <Card className="strpied-tabled-with-hover">
      <Card.Header>
        <Card.Title as="h4">アカウントリスト</Card.Title>
        <div className="mb-2 d-flex flex-row justify-content-between align-items-center">
          <PaginationComponent
            currentPage={currentPage}
            totalItems={users.length}
            itemsPerPage={NUM_ITEMS_PER_PAGE}
            onPageChange={handleChangePage}
          />
          <Button
            className="btn-outline btn-wd mr-1"
            variant="default"
            onClick={() => history.push(`/admin/governments/${governmentId}/accounts/create`)}
          >
            {BUTTON_LABELS.BUTTON_REGISTER_NEW}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="table-responsive p-0 overflow-auto">
        {loading ? <Spinner/> : <>
          {users.length || initialLoad ? <Table className="table-hover table-striped w-full">
            <thead>
              <tr>
                <th>ユーザID</th>
                <th>グループ</th>
                <th style={{textAlign: 'center'}}>有効</th>
                <th>アクション</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                if (index < currentPage * NUM_ITEMS_PER_PAGE || index >= (currentPage + 1) * NUM_ITEMS_PER_PAGE) return null;
                return (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user?.groups && user.groups.length > 0 ? getRoleLabels(user.groups) : 'No Group'}</td>
                    <td align="center">
                      <Switch onChange={() => toggleUserStatus(user)} checked={user.enabled || user.enabled === null}/>
                    </td>
                    <td>
                      <Button className="mr-3 btn-outline" onClick={() => history.push(`/admin/accounts/${user.id}/edit`)}>{BUTTON_LABELS.BUTTON_EDIT}</Button>
                      <Button className="mr-3 btn-outline" onClick={() => handleDeleteUserClick(user.id)}>{BUTTON_LABELS.BUTTON_DELETE}</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table> : <p style={{textAlign: 'center'}}>{NOTIFICATION_MESSAGES.HAVE_NO_USER}</p>}
        </>}
      </Card.Body>
    </Card>
  </Col>
}

export default Accounts;
